import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BetaLockOverlay } from '../../components/ui/BetaLockOverlay';
import { 
    Award, BookOpen, Shield, TrendingUp, Scale, Smartphone,
    ChevronRight, ChevronLeft, Check, X, Clock, Trophy,
    AlertCircle, Sparkles, Lock, CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

// Certification card component
const CertificationCard = ({ cert, onStart, isLocked, cooldownEnd, certStats }) => {
    const iconMap = {
        'award': Award,
        'trending-up': TrendingUp,
        'handshake': Scale,
        'scale': Scale,
        'smartphone': Smartphone
    };
    
    const Icon = iconMap[cert.icon] || BookOpen;
    const difficultyColors = {
        easy: 'bg-green-500/20 text-green-400',
        medium: 'bg-yellow-500/20 text-yellow-400',
        hard: 'bg-red-500/20 text-red-400'
    };
    
    const cooldownRemaining = cooldownEnd ? Math.max(0, Math.ceil((new Date(cooldownEnd) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
    
    return (
        <motion.div
            className={`bg-card backdrop-blur-xl rounded-2xl p-6 border ${
                cert.passed ? 'border-green-500/30' : isLocked ? 'border-border opacity-60' : 'border-border'
            }`}
            whileHover={!isLocked ? { scale: 1.02 } : {}}
        >
            <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl ${cert.passed ? 'bg-green-500/20' : 'bg-purple-500/20'}`}>
                    <Icon className={cert.passed ? 'text-green-400' : 'text-purple-400'} size={28} />
                </div>
                {cert.passed && (
                    <div className="flex items-center gap-1 text-green-400 text-sm">
                        <CheckCircle size={16} />
                        <span>Certifié</span>
                    </div>
                )}
                {isLocked && !cert.passed && (
                    <Lock className="text-muted-foreground" size={20} />
                )}
            </div>
            
            <h3 className="text-lg font-bold text-white mb-2">{cert.name}</h3>
            <p className="text-muted-foreground text-sm mb-4">{cert.description}</p>
            
            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className={`px-2 py-1 rounded-full text-xs ${difficultyColors[cert.difficulty]}`}>
                    {cert.difficulty === 'easy' ? 'Facile' : cert.difficulty === 'medium' ? 'Moyen' : 'Difficile'}
                </span>
                <span className="text-muted-foreground text-xs">{cert.questions_count || 8} questions</span>
                <span className="text-muted-foreground text-xs">{cert.pass_threshold}% requis</span>
            </div>
            
            {/* Personal stats */}
            {certStats && (
                <div className="bg-white/5 rounded-lg p-2.5 mb-3 flex items-center gap-3">
                    <div className="flex-1">
                        <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Meilleur score</span>
                            <span className={`font-bold ${certStats.best_score >= cert.pass_threshold ? 'text-green-400' : 'text-orange-400'}`}>{certStats.best_score}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${certStats.best_score >= cert.pass_threshold ? 'bg-green-500' : 'bg-orange-500'}`} style={{width: `${Math.min(certStats.best_score, 100)}%`}} />
                        </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{certStats.attempts} essai{certStats.attempts > 1 ? 's' : ''}</span>
                </div>
            )}
            
            {/* Rewards */}
            <div className="bg-black/20 rounded-xl p-3 mb-4">
                <div className="text-xs text-muted-foreground mb-2">Récompenses</div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                        <Sparkles size={14} className="text-yellow-400" />
                        <span className="text-white text-sm font-medium">+{cert.rewards?.elo_bonus || 15} Elo</span>
                    </div>
                    {cert.rewards?.badge && (
                        <div className="flex items-center gap-1">
                            <Award size={14} className="text-purple-400" />
                            <span className="text-white text-sm">Badge</span>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Action button */}
            {cooldownRemaining > 0 ? (
                <button 
                    disabled
                    className="w-full py-3 bg-gray-700 text-muted-foreground rounded-xl font-medium flex items-center justify-center gap-2"
                >
                    <Clock size={18} />
                    Réessayer dans {cooldownRemaining}j
                </button>
            ) : cert.passed ? (
                <button 
                    disabled
                    className="w-full py-3 bg-green-500/20 text-green-400 rounded-xl font-medium flex items-center justify-center gap-2"
                >
                    <Trophy size={18} />
                    Certification obtenue
                </button>
            ) : isLocked ? (
                <button 
                    disabled
                    className="w-full py-3 bg-gray-700 text-muted-foreground rounded-xl font-medium"
                >
                    Certification verrouillée
                </button>
            ) : (
                <button 
                    onClick={() => onStart(cert.id)}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
                >
                    Commencer
                    <ChevronRight size={18} />
                </button>
            )}
        </motion.div>
    );
};

// Quiz question component
const QuizQuestion = ({ question, selectedAnswer, onSelect, showResult }) => {
    return (
        <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-6">{question.question}</h3>
            
            <div className="space-y-3">
                {question.options.map((option) => {
                    const isSelected = selectedAnswer === option.id;
                    const isCorrect = option.id === question.correct_answer;
                    
                    let bgClass = 'bg-card border-border hover:border-purple-500/50';
                    if (showResult) {
                        if (isCorrect) {
                            bgClass = 'bg-green-500/20 border-green-500';
                        } else if (isSelected && !isCorrect) {
                            bgClass = 'bg-red-500/20 border-red-500';
                        }
                    } else if (isSelected) {
                        bgClass = 'bg-purple-500/20 border-purple-500';
                    }
                    
                    return (
                        <motion.button
                            key={option.id}
                            onClick={() => !showResult && onSelect(option.id)}
                            disabled={showResult}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${bgClass}`}
                            whileTap={!showResult ? { scale: 0.98 } : {}}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    showResult && isCorrect ? 'bg-green-500 text-white' :
                                    showResult && isSelected && !isCorrect ? 'bg-red-500 text-white' :
                                    isSelected ? 'bg-purple-500 text-white' :
                                    'bg-muted text-muted-foreground'
                                }`}>
                                    {showResult && isCorrect ? <Check size={16} /> :
                                     showResult && isSelected && !isCorrect ? <X size={16} /> :
                                     option.id.toUpperCase()}
                                </div>
                                <span className={`${showResult && isCorrect ? 'text-green-300' : 'text-white'}`}>
                                    {option.text}
                                </span>
                            </div>
                        </motion.button>
                    );
                })}
            </div>
            
            {/* Explanation */}
            {showResult && question.explanation && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl"
                >
                    <div className="flex items-start gap-2">
                        <AlertCircle className="text-blue-400 mt-0.5" size={18} />
                        <p className="text-blue-200 text-sm">{question.explanation}</p>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

// Main Certifications Page
const CertificationsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [certifications, setCertifications] = useState([]);
    const [myStats, setMyStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showResult, setShowResult] = useState(false);
    const [quizResult, setQuizResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    
    useEffect(() => {
        loadCertifications();
    }, []);
    
    const loadCertifications = async () => {
        try {
            const [certsRes, statsRes] = await Promise.all([
                api.get('/reputation/certifications'),
                api.get('/reputation/certifications/my-stats').catch(() => ({ data: null }))
            ]);
            setCertifications(certsRes.data.certifications || []);
            setMyStats(statsRes.data);
        } catch (err) {
            console.error('Error loading certifications:', err);
            // Fallback data
            setCertifications([
                {
                    id: 'exclusivite',
                    name: 'Expert Exclusivité',
                    description: 'Maîtrisez les arguments pour obtenir des mandats exclusifs',
                    icon: 'award',
                    difficulty: 'easy',
                    questions_count: 8,
                    pass_threshold: 75,
                    rewards: { elo_bonus: 15, badge: 'cert_exclusivite' },
                    passed: false
                },
                {
                    id: 'prix',
                    name: 'Stratège Prix',
                    description: 'Estimez les biens avec précision et justifiez vos recommandations',
                    icon: 'trending-up',
                    difficulty: 'medium',
                    questions_count: 10,
                    pass_threshold: 70,
                    rewards: { elo_bonus: 20, badge: 'cert_prix' },
                    passed: false
                },
                {
                    id: 'negociation',
                    name: 'As de la Négociation',
                    description: 'Techniques avancées pour conclure au meilleur prix',
                    icon: 'handshake',
                    difficulty: 'medium',
                    questions_count: 8,
                    pass_threshold: 75,
                    rewards: { elo_bonus: 20, badge: 'cert_nego' },
                    passed: false
                },
                {
                    id: 'juridique',
                    name: 'Juriste Immo',
                    description: 'Connaissances juridiques essentielles pour sécuriser vos transactions',
                    icon: 'scale',
                    difficulty: 'hard',
                    questions_count: 12,
                    pass_threshold: 80,
                    rewards: { elo_bonus: 25, badge: 'cert_juridique' },
                    passed: false
                },
                {
                    id: 'digital',
                    name: 'Agent Digital',
                    description: 'Maîtrisez les outils numériques pour booster votre activité',
                    icon: 'smartphone',
                    difficulty: 'easy',
                    questions_count: 8,
                    pass_threshold: 70,
                    rewards: { elo_bonus: 10, badge: 'cert_digital' },
                    passed: false
                }
            ]);
        } finally {
            setLoading(false);
        }
    };
    
    const startQuiz = async (certificationId) => {
        try {
            setLoading(true);
            const response = await api.get(`/reputation/certifications/${certificationId}/questions`);
            setQuestions(response.data.questions || []);
            setActiveQuiz(certificationId);
            setCurrentQuestion(0);
            setAnswers({});
            setShowResult(false);
            setQuizResult(null);
        } catch (err) {
            console.error('Error loading questions:', err);
        } finally {
            setLoading(false);
        }
    };
    
    const handleAnswer = (answerId) => {
        setAnswers(prev => ({
            ...prev,
            [questions[currentQuestion].id]: answerId
        }));
    };
    
    const nextQuestion = () => {
        if (showResult) {
            setShowResult(false);
            if (currentQuestion < questions.length - 1) {
                setCurrentQuestion(prev => prev + 1);
            } else {
                submitQuiz();
            }
        } else {
            setShowResult(true);
        }
    };
    
    const submitQuiz = async () => {
        try {
            setSubmitting(true);
            const response = await api.post(`/reputation/certifications/${activeQuiz}/submit`, {
                answers
            });
            setQuizResult(response.data);
            
            // Refresh certifications
            loadCertifications();
        } catch (err) {
            console.error('Error submitting quiz:', err);
            // Calculate result locally
            let correct = 0;
            questions.forEach(q => {
                if (answers[q.id] === q.correct_answer) correct++;
            });
            const score = Math.round((correct / questions.length) * 100);
            const cert = certifications.find(c => c.id === activeQuiz);
            setQuizResult({
                passed: score >= (cert?.pass_threshold || 70),
                score,
                correct_count: correct,
                total_questions: questions.length,
                rewards: score >= (cert?.pass_threshold || 70) ? cert?.rewards : null
            });
        } finally {
            setSubmitting(false);
        }
    };
    
    const exitQuiz = () => {
        setActiveQuiz(null);
        setQuestions([]);
        setCurrentQuestion(0);
        setAnswers({});
        setShowResult(false);
        setQuizResult(null);
        setShowDetails(false);
        loadCertifications();
    };
    
    if (loading && !certifications.length) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            </div>
        );
    }
    
    // Quiz result screen
    if (quizResult) {
        const questionResults = quizResult.question_results || [];
        const wrongAnswers = questionResults.filter(q => !q.is_correct);
        
        return (
            <div className="min-h-screen bg-background p-4">
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        className="bg-gray-800 rounded-3xl p-8 text-center mb-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                    >
                        <motion.div
                            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                                quizResult.passed ? 'bg-green-500/20' : 'bg-orange-500/20'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                        >
                            {quizResult.passed ? (
                                <Trophy size={40} className="text-green-400" />
                            ) : (
                                <BookOpen size={40} className="text-orange-400" />
                            )}
                        </motion.div>
                        
                        <h2 className={`text-2xl font-bold mb-1 ${quizResult.passed ? 'text-green-400' : 'text-orange-400'}`}>
                            {quizResult.passed ? 'Certification Obtenue !' : 'Continuez vos efforts !'}
                        </h2>
                        
                        <div className="text-5xl font-bold text-white my-4">
                            {quizResult.score}%
                        </div>
                        
                        <p className="text-muted-foreground mb-2">
                            {quizResult.correct_count} / {quizResult.total_questions} bonnes réponses
                            {wrongAnswers.length > 0 && (
                                <span className="text-orange-400 ml-2">({wrongAnswers.length} erreur{wrongAnswers.length > 1 ? 's' : ''})</span>
                            )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Score requis : {quizResult.required_score}%
                        </p>
                        
                        {quizResult.passed && quizResult.rewards && (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 mt-4 inline-block">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="text-yellow-400" size={18} />
                                    <span className="text-white font-bold">+{quizResult.rewards.elo_bonus} Elo</span>
                                </div>
                            </div>
                        )}
                        
                        <div className="flex gap-3 mt-6 justify-center">
                            <button
                                onClick={exitQuiz}
                                className="px-6 py-3 bg-muted hover:bg-white/20 text-white rounded-xl font-medium transition-colors"
                            >
                                Retour aux certifications
                            </button>
                            {questionResults.length > 0 && (
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-medium"
                                >
                                    {showDetails ? 'Masquer' : 'Voir'} les corrections
                                </button>
                            )}
                        </div>
                    </motion.div>
                    
                    {/* Detailed corrections */}
                    {showDetails && questionResults.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-4"
                        >
                            <h3 className="text-lg font-bold text-white">Corrections détaillées</h3>
                            {questionResults.map((qr, idx) => (
                                <div key={qr.id} className={`bg-card border rounded-xl p-4 ${qr.is_correct ? 'border-green-500/20' : 'border-red-500/30'}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                            qr.is_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                        }`}>
                                            {qr.is_correct ? '✓' : '✗'}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-white mb-2">
                                                {idx + 1}. {qr.question}
                                            </p>
                                            {!qr.is_correct && (
                                                <>
                                                    <div className="text-xs mb-1">
                                                        <span className="text-red-400">Votre réponse : </span>
                                                        <span className="text-white/70">
                                                            {qr.options?.find(o => o.id === qr.user_answer)?.text || 'Non répondu'}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs mb-2">
                                                        <span className="text-green-400">Bonne réponse : </span>
                                                        <span className="text-white/70">
                                                            {qr.options?.find(o => o.id === qr.correct_answer)?.text}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            {qr.explanation && (
                                                <p className="text-xs text-muted-foreground mt-1 p-2 bg-white/5 rounded-lg">
                                                    {qr.explanation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>
        );
    }
    
    // Active quiz screen
    if (activeQuiz && questions.length > 0) {
        const question = questions[currentQuestion];
        const progress = ((currentQuestion + 1) / questions.length) * 100;
        
        return (
            <div className="min-h-screen bg-background py-8 px-4">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <button
                            onClick={exitQuiz}
                            className="text-muted-foreground hover:text-white flex items-center gap-2"
                        >
                            <ChevronLeft size={20} />
                            Quitter
                        </button>
                        <span className="text-muted-foreground">
                            Question {currentQuestion + 1} / {questions.length}
                        </span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="h-2 bg-gray-800 rounded-full mb-8 overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                        />
                    </div>
                    
                    {/* Question */}
                    <div className="bg-card backdrop-blur-xl rounded-2xl p-6 border border-border">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentQuestion}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <QuizQuestion
                                    question={question}
                                    selectedAnswer={answers[question.id]}
                                    onSelect={handleAnswer}
                                    showResult={showResult}
                                />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    
                    {/* Next button */}
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={nextQuestion}
                            disabled={!answers[question.id]}
                            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {showResult ? (
                                currentQuestion < questions.length - 1 ? 'Question suivante' : 'Voir les résultats'
                            ) : (
                                'Valider'
                            )}
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Certifications list
    return (
        <BetaLockOverlay featureName="La section Certifications">
        <div className="min-h-screen bg-background py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(user?.role === 'admin' ? '/admin/reputation' : '/agent/reputation')}
                        className="text-muted-foreground hover:text-white flex items-center gap-2 mb-4"
                    >
                        <ChevronLeft size={20} />
                        Retour à la réputation
                    </button>
                    <h1 className="text-3xl font-bold text-white mb-2">Certifications</h1>
                    <p className="text-muted-foreground">
                        Validez vos compétences et gagnez des bonus Elo
                    </p>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-card backdrop-blur-xl rounded-xl p-4 text-center border border-border">
                        <div className="text-2xl font-bold text-white">
                            {certifications.filter(c => c.passed).length}
                        </div>
                        <div className="text-muted-foreground text-sm">Obtenues</div>
                    </div>
                    <div className="bg-card backdrop-blur-xl rounded-xl p-4 text-center border border-border">
                        <div className="text-2xl font-bold text-white">
                            {certifications.length - certifications.filter(c => c.passed).length}
                        </div>
                        <div className="text-muted-foreground text-sm">Disponibles</div>
                    </div>
                    <div className="bg-card backdrop-blur-xl rounded-xl p-4 text-center border border-border">
                        <div className="text-2xl font-bold text-yellow-400">
                            +{certifications.filter(c => c.passed).reduce((sum, c) => sum + (c.rewards?.elo_bonus || 0), 0)}
                        </div>
                        <div className="text-muted-foreground text-sm">HomlyScore gagné</div>
                    </div>
                </div>
                
                {/* Certifications grid */}
                <div className="grid md:grid-cols-2 gap-6">
                    {certifications.map(cert => {
                        const stats = myStats?.certifications?.find(c => c.certification_id === cert.id);
                        return (
                            <CertificationCard
                                key={cert.id}
                                cert={cert}
                                onStart={startQuiz}
                                isLocked={false}
                                cooldownEnd={cert.cooldown_end}
                                certStats={stats}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
        </BetaLockOverlay>
    );
};

export default CertificationsPage;
